import React, { useCallback, useEffect, useRef } from "react";

import Svg, { Path } from "react-native-svg";
import Animated, {
  useSharedValue,
  useAnimatedProps,
  useAnimatedStyle,
  withTiming,
  withDelay,
  interpolateColor,
  withSequence,
  withSpring,
} from "react-native-reanimated";
import { useScaleFactor } from "./AHScaleFactor";
import { CircleSize, useCircleSize } from "./LayoutProvider";
import { useTheme } from "./AHTheme/AHTheme";

const AnimatedPath = Animated.createAnimatedComponent(Path);

export function TodoStatusAnimatedCircle(props: {
  size: CircleSize;
  status: "DONE" | "PENDING";
  isExpired: boolean;
}) {
  const figureProgress = useSharedValue(props.status === "DONE" ? 1 : 0);
  const fillOpacity = useSharedValue(props.status === "DONE" ? 1 : 0);
  const rippleScale = useSharedValue(0);
  const rippleOpacity = useSharedValue(0);
  const pressScale = useSharedValue(1);

  const animatedProps = useAnimatedProps(() => ({
    strokeDashoffset: 25 * (1 - figureProgress.value),
  }));

  const triggerDone = useCallback(
    (animated: boolean) => {
      fillOpacity.value = animated ? withTiming(1, { duration: 150 }) : 1;
      if (animated) {
        figureProgress.value = 0;
        figureProgress.value = withDelay(200, withTiming(1, { duration: 180 }));
      } else {
        figureProgress.value = 1;
      }
    },
    [figureProgress, fillOpacity]
  );
  const triggerPending = useCallback(
    (animated: boolean) => {
      fillOpacity.value = animated ? withTiming(0, { duration: 150 }) : 0;

      figureProgress.value = 0;
    },
    [figureProgress, fillOpacity]
  );

  const triggerRipple = useCallback(() => {
    rippleScale.value = 0;
    rippleScale.value = withSpring(1, { duration: 320 });

    rippleOpacity.value = 0;
    rippleOpacity.value = withSequence(
      withTiming(0.2, { duration: 100 }),
      withTiming(0, { duration: 300 })
    );
  }, [rippleScale, rippleOpacity]);
  const prevState = usePrevious(props.status);
  useEffect(() => {
    const shouldAnimate = prevState ? prevState !== props.status : false;

    if (props.status === "DONE") {
      triggerDone(shouldAnimate);
    } else {
      triggerPending(shouldAnimate);
    }
    if (shouldAnimate) {
      triggerRipple();
    }
  }, [prevState, props.status, triggerDone, triggerPending, triggerRipple]);

  const sf = useScaleFactor();

  const theme = useTheme();
  const fillColor =
    props.status === "DONE" ? theme["status-positive"] : theme.transparent;
  const tintColor =
    props.status === "DONE"
      ? theme["status-positive"]
      : props.isExpired
        ? theme["status-negative"]
        : theme["status-active"];
  const absoluteSize = useCircleSize(props.size);
  const fillStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      fillOpacity.value,
      [0, 1],
      [fillColor, tintColor]
    ),
    width: absoluteSize,
    height: absoluteSize,
    borderRadius: absoluteSize / 2,
    borderWidth: Math.max(1, Math.round(2 * sf)),
    borderColor: tintColor,
    transform: [{ scale: pressScale.value }],
    alignItems: "center",
    justifyContent: "center",
  }));

  const rippleStyle = useAnimatedStyle(() => ({
    opacity: rippleOpacity.value,
    width: absoluteSize,
    height: absoluteSize,
    borderRadius: absoluteSize / 2,
    backgroundColor: tintColor,
    transform: [{ scale: rippleScale.value * 2 }],
    position: "absolute",
  }));
  return (
    <Animated.View style={fillStyle}>
      <Animated.View style={[rippleStyle]} />

      <Svg
        width={absoluteSize * 0.6}
        height={absoluteSize * 0.6}
        viewBox="0 0 24 24"
        style={{ zIndex: 1 }}
      >
        <AnimatedPath
          d="M4 12l5 5L20 6"
          fill="none"
          stroke="white"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={25}
          animatedProps={animatedProps}
        />
      </Svg>
    </Animated.View>
  );
}
function usePrevious<T>(value: T): T | null {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}
